/**
 * Saudi HR helpers — contracts, recruitment, terminations (KSA Labor Law).
 */
import {
  calculateSaudiAllowances,
  calculateEOSBProvision,
  DEFAULT_HOUSING_ALLOWANCE_PCT,
  isSaudiNational,
} from '../../salaires/utils/payrollSaudiFormat';
import {
  formatBrandMoney,
  formatBrandMoneyCompact,
  getBrandLocale,
} from '../../../shared/utils/brandCurrency';
import {
  getBrandPhonePlaceholder,
  isBrandPhoneValid,
  normalizeBrandPhone,
  SAUDI_PHONE_PREFIX,
} from '../../../shared/utils/brandCountry';

export { isSaudiNational, SAUDI_PHONE_PREFIX };

/** Saudi contract type identifiers (stored in DB). */
export const SAUDI_CONTRACT_TYPES = {
  UNLIMITED: 'unlimited',
  LIMITED: 'limited',
  PART_TIME: 'part_time',
  TEMPORARY: 'temporary',
};

/** Map legacy French/Algerian contract codes to Saudi types. */
export const LEGACY_CONTRACT_TYPE_MAP = {
  cdi: SAUDI_CONTRACT_TYPES.UNLIMITED,
  CDI: SAUDI_CONTRACT_TYPES.UNLIMITED,
  cdd: SAUDI_CONTRACT_TYPES.LIMITED,
  CDD: SAUDI_CONTRACT_TYPES.LIMITED,
  stage: SAUDI_CONTRACT_TYPES.TEMPORARY,
  autre: SAUDI_CONTRACT_TYPES.PART_TIME,
};

export const SAUDI_PROBATION_DAYS_DEFAULT = 90;
export const SAUDI_PROBATION_DAYS_MAX = 180;

/** Notice period in calendar days (Saudi Labor Law Art. 75). */
export const NOTICE_PERIOD_MONTHLY_DAYS = 30;
export const NOTICE_PERIOD_OTHER_DAYS = 60;

/** Saudi departure / termination types. */
export const SAUDI_DEPARTURE_TYPES = {
  RESIGNATION: 'resignation',
  TERMINATION: 'termination',
  CONTRACT_END: 'contract_end',
  RETIREMENT: 'retirement',
  MUTUAL_AGREEMENT: 'mutual_agreement',
};

/** Map legacy French departure codes. */
export const LEGACY_DEPARTURE_TYPE_MAP = {
  démission: SAUDI_DEPARTURE_TYPES.RESIGNATION,
  demission: SAUDI_DEPARTURE_TYPES.RESIGNATION,
  licenciement: SAUDI_DEPARTURE_TYPES.TERMINATION,
  retraite: SAUDI_DEPARTURE_TYPES.RETIREMENT,
  fin_contrat: SAUDI_DEPARTURE_TYPES.CONTRACT_END,
  mutation: SAUDI_DEPARTURE_TYPES.MUTUAL_AGREEMENT,
};

export const RECRUITMENT_CHANNELS = [
  { value: 'taqat', labelKey: 'channel_taqat' },
  { value: 'linkedin', labelKey: 'channel_linkedin' },
  { value: 'bayt', labelKey: 'channel_bayt' },
  { value: 'internal', labelKey: 'channel_internal' },
  { value: 'other', labelKey: 'channel_other' },
];

export const NITAQAT_LEVELS = [
  { value: 'platinum', labelKey: 'nitaqat_platinum' },
  { value: 'green', labelKey: 'nitaqat_green' },
  { value: 'yellow', labelKey: 'nitaqat_yellow' },
  { value: 'red', labelKey: 'nitaqat_red' },
];

/** Normalize stored contract type to Saudi identifier. */
export function normalizeContractType(type) {
  if (!type) return SAUDI_CONTRACT_TYPES.UNLIMITED;
  const key = String(type).trim();
  if (Object.values(SAUDI_CONTRACT_TYPES).includes(key)) return key;
  return LEGACY_CONTRACT_TYPE_MAP[key] || LEGACY_CONTRACT_TYPE_MAP[key.toLowerCase()] || SAUDI_CONTRACT_TYPES.UNLIMITED;
}

/** Normalize stored departure type to Saudi identifier. */
export function normalizeDepartureType(type) {
  if (!type) return '';
  const key = String(type).trim();
  if (Object.values(SAUDI_DEPARTURE_TYPES).includes(key)) return key;
  return LEGACY_DEPARTURE_TYPE_MAP[key] || LEGACY_DEPARTURE_TYPE_MAP[key.toLowerCase()] || key;
}

/** ar-SA date formatting for HR views. */
export function formatHrDate(value, language) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(getBrandLocale(language), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Probation end date from start + days (default 90). */
export function calculateProbationEndDate(startDate, probationDays = SAUDI_PROBATION_DAYS_DEFAULT) {
  if (!startDate) return null;
  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) return null;
  const end = new Date(start);
  end.setDate(end.getDate() + Math.min(Math.max(probationDays, 0), SAUDI_PROBATION_DAYS_MAX));
  return end.toISOString().split('T')[0];
}

/** Notice period end date from notice start. */
export function calculateNoticeEndDate(noticeStartDate, paymentFrequency = 'monthly') {
  if (!noticeStartDate) return null;
  const start = new Date(noticeStartDate);
  if (Number.isNaN(start.getTime())) return null;
  const days = paymentFrequency === 'monthly' ? NOTICE_PERIOD_MONTHLY_DAYS : NOTICE_PERIOD_OTHER_DAYS;
  const end = new Date(start);
  end.setDate(end.getDate() + days);
  return end.toISOString().split('T')[0];
}

/** Notice period in days based on salary payment frequency. */
export function getNoticePeriodDays(paymentFrequency = 'monthly') {
  return paymentFrequency === 'monthly' ? NOTICE_PERIOD_MONTHLY_DAYS : NOTICE_PERIOD_OTHER_DAYS;
}

/** Years of service between two dates (fractional). */
export function calculateYearsOfService(startDate, endDate = new Date()) {
  if (!startDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  return Math.max(0, (end - start) / (365.25 * 24 * 60 * 60 * 1000));
}

/**
 * EOSB (End-of-Service Benefits) per Saudi Labor Law Art. 84–87 (simplified).
 * @param {object} params
 * @param {number} params.basicSalary — monthly basic (GOSI/EOSB base)
 * @param {string|Date} params.startDate
 * @param {string|Date} params.endDate
 * @param {string} params.departureType — resignation | termination | contract_end | retirement | mutual_agreement
 * @returns {{ grossEOSB, payableEOSB, yearsOfService, monthlyAccrual }}
 */
export function calculateTerminationEOSB({
  basicSalary,
  startDate,
  endDate,
  departureType,
}) {
  const basic = Math.max(parseFloat(basicSalary) || 0, 0);
  const years = calculateYearsOfService(startDate, endDate);
  const type = normalizeDepartureType(departureType);

  if (basic <= 0 || years <= 0) {
    return { grossEOSB: 0, payableEOSB: 0, yearsOfService: years, monthlyAccrual: 0 };
  }

  // Full EOSB: ½ month/year for first 5 years, 1 month/year thereafter
  const firstFive = Math.min(years, 5);
  const afterFive = Math.max(years - 5, 0);
  const grossEOSB = basic * (firstFive * 0.5 + afterFive * 1);

  let payableRatio = 1;

  if (type === SAUDI_DEPARTURE_TYPES.RESIGNATION) {
    if (years < 2) payableRatio = 0;
    else if (years < 5) payableRatio = 1 / 3;
    else if (years < 10) payableRatio = 2 / 3;
    else payableRatio = 1;
  } else if (type === SAUDI_DEPARTURE_TYPES.TERMINATION) {
    // Employer-initiated without employee fault — full EOSB
    payableRatio = 1;
  } else if (type === SAUDI_DEPARTURE_TYPES.CONTRACT_END || type === SAUDI_DEPARTURE_TYPES.MUTUAL_AGREEMENT) {
    payableRatio = 1;
  } else if (type === SAUDI_DEPARTURE_TYPES.RETIREMENT) {
    payableRatio = 1;
  }

  const payableEOSB = Math.round(grossEOSB * payableRatio * 100) / 100;
  const monthlyAccrual = calculateEOSBProvision(basic);

  return {
    grossEOSB: Math.round(grossEOSB * 100) / 100,
    payableEOSB,
    yearsOfService: Math.round(years * 100) / 100,
    monthlyAccrual,
    payableRatio,
  };
}

/** Build Saudi salary package from basic + optional allowance overrides. */
export function buildSaudiSalaryPackage(basicSalary, allowanceOverrides = {}) {
  const basic = parseFloat(basicSalary) || 0;
  const allowances = calculateSaudiAllowances(basic, allowanceOverrides);
  const totalPackage = basic + allowances.total;
  return {
    basicSalary: basic,
    housingAllowance: allowances.housing,
    transportAllowance: allowances.transport,
    mobileAllowance: allowances.mobile,
    foodAllowance: allowances.food,
    totalPackage: Math.round(totalPackage * 100) / 100,
    housingPct: allowanceOverrides.housingPct ?? DEFAULT_HOUSING_ALLOWANCE_PCT,
  };
}

/** Contract types requiring a fixed end date. */
export function contractTypeRequiresEndDate(contractType) {
  const t = normalizeContractType(contractType);
  return t === SAUDI_CONTRACT_TYPES.LIMITED || t === SAUDI_CONTRACT_TYPES.TEMPORARY;
}

/** Default empty Saudi contract remuneration fields. */
export function getDefaultSaudiContractFields() {
  return {
    contractType: SAUDI_CONTRACT_TYPES.UNLIMITED,
    probationPeriod: SAUDI_PROBATION_DAYS_DEFAULT,
    probationPeriodUnit: 'days',
    paymentFrequency: 'monthly',
    noticePeriodDays: NOTICE_PERIOD_MONTHLY_DAYS,
    basicSalary: 0,
    housingAllowance: 0,
    transportAllowance: 0,
    mobileAllowance: 0,
    foodAllowance: 0,
    totalPackage: 0,
  };
}

/** Default Saudi recruitment candidate fields. */
export function getDefaultSaudiRecruitmentFields() {
  return {
    nationality: 'SA',
    isSaudi: true,
    nitaqatLevel: '',
    workPermitRequired: false,
    workPermitStatus: '',
    iqamaRequired: false,
    iqamaStatus: '',
    medicalFitness: false,
    backgroundCheck: false,
    recruitmentChannel: '',
    recruitmentCost: 0,
  };
}

/** Default Saudi departure fields. */
export function getDefaultSaudiDepartureFields() {
  return {
    noticePeriodDays: NOTICE_PERIOD_MONTHLY_DAYS,
    eosbAmount: 0,
    exitVisaRequired: false,
    exitVisaStatus: '',
  };
}

/* ── Employee identity, locale, phone, currency (HR pages) ── */

/** Major Saudi cities for address forms. */
export const SAUDI_CITIES = [
  'الرياض', 'جدة', 'مكة المكرمة', 'المدينة المنورة', 'الدمام', 'الخبر',
  'الظهران', 'الطائف', 'بريدة', 'تبوك', 'خميس مشيط', 'أبها', 'حائل',
  'نجران', 'جazan', 'الجبيل', 'ينبع', 'الأحساء', 'القطيف', 'عرعر',
];

/** Saudi administrative regions. */
export const SAUDI_REGIONS = [
  'الرياض', 'مكة المكرمة', 'المدينة المنورة', 'القصيم', 'الشرقية', 'عسير',
  'تبوك', 'حائل', 'الحدود الشمالية', 'جazan', 'نجران', 'الباحة', 'الجوف',
];

/** Nitaqat categories for employee records. */
export const NITAQAT_CATEGORIES = NITAQAT_LEVELS.map(({ value, labelKey }) => ({
  value,
  label: labelKey,
}));

/** Department budget tier thresholds (SAR). */
export const SAUDI_BUDGET_TIERS = { lowMax: 50000, midMax: 200000 };

/** Alias for legacy imports. */
export function normalizeLegacyContractType(type) {
  return normalizeContractType(type);
}

export function getHrDateLocale(language) {
  return getBrandLocale(language);
}

export function formatHrMoney(amount) {
  return formatBrandMoney(amount);
}

export function formatHrMoneyCompact(amount) {
  return formatBrandMoneyCompact(amount);
}

export function getHrPhonePlaceholder() {
  return getBrandPhonePlaceholder();
}

export function validateHrPhone(value) {
  return isBrandPhoneValid(value);
}

export function normalizeHrPhone(value) {
  return normalizeBrandPhone(value);
}

export function formatHrPhone(value) {
  const normalized = normalizeHrPhone(value);
  if (!normalized) return '—';
  if (normalized.startsWith(SAUDI_PHONE_PREFIX)) {
    const digits = normalized.replace(/\D/g, '').slice(3);
    if (digits.length >= 9) {
      return `${SAUDI_PHONE_PREFIX} ${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 9)}`.trim();
    }
  }
  return normalized;
}

/** Contract options for employee forms. */
export function getSaudiContractOptions(t) {
  return [
    { value: SAUDI_CONTRACT_TYPES.UNLIMITED, label: t('modules.ressources_humaines.shared.contract_permanent') },
    { value: SAUDI_CONTRACT_TYPES.LIMITED, label: t('modules.ressources_humaines.shared.contract_fixed_term') },
    { value: SAUDI_CONTRACT_TYPES.PART_TIME, label: t('modules.ressources_humaines.shared.contract_part_time') },
    { value: SAUDI_CONTRACT_TYPES.TEMPORARY, label: t('modules.ressources_humaines.shared.contract_seasonal') },
  ];
}

export function getSaudiContractLabel(type, t) {
  const normalized = normalizeContractType(type);
  const opt = getSaudiContractOptions(t).find((o) => o.value === normalized);
  return opt?.label || normalized;
}

export function getEmployeeNationalityType(employee = {}) {
  if (employee.nationalityType === 'saudi' || employee.nationalityType === 'expatriate') {
    return employee.nationalityType;
  }
  return isSaudiNational(employee) ? 'saudi' : 'expatriate';
}

/** Default Saudi employee identity/residency fields. */
export function getDefaultSaudiEmployeeFields() {
  return {
    nationalityType: 'saudi',
    nationalId: '',
    iqamaNumber: '',
    iqamaExpiry: '',
    passportNumber: '',
    passportExpiry: '',
    sponsorship: '',
    workPermitNumber: '',
    workPermitExpiry: '',
    exitReentryVisa: '',
    exitReentryExpiry: '',
    gosiNumber: '',
    nitaqatCategory: '',
    iban: '',
    region: '',
  };
}

/** Merge API record with Saudi defaults and legacy field mappings. */
export function normalizeEmployeeForSaudi(record = {}) {
  const defaults = getDefaultSaudiEmployeeFields();
  return {
    ...defaults,
    ...record,
    nationalityType: record.nationalityType || getEmployeeNationalityType(record),
    typeContrat: normalizeContractType(record.typeContrat),
    gosiNumber: record.gosiNumber || record.securiteSociale || '',
    iban: record.iban || record.numCcp || '',
    region: record.region || record.ville || record.city || '',
    periodeEssai: record.periodeEssai || String(SAUDI_PROBATION_DAYS_DEFAULT),
    telephone: normalizeHrPhone(record.telephone || record.phone || ''),
  };
}
